import { useEffect, useId, useRef, useState } from "react";
import { exportCurrentProject } from "../project/projectBundle";
import { exportBedrockSkinPack } from "../skin/bedrockPack";
import { useProjectLibraryStore } from "../store/project-library-store";
import { useSkinStore } from "../store/skinStore";
import { cx } from "../styles/cx";
import ui from "../styles/ui.module.css";
import styles from "./AppHeader.module.css";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { Icon } from "./ui/Icon";

interface AppHeaderProps {
  onNotify: (message: string, tone?: "success" | "error") => void;
}

export function AppHeader({ onNotify }: AppHeaderProps) {
  const document = useSkinStore((state) => state.document);
  const newBlank = useSkinStore((state) => state.newBlank);
  const newDefault = useSkinStore((state) => state.newDefault);
  const importJavaPlayer = useSkinStore((state) => state.importJavaPlayer);
  const importPng = useSkinStore((state) => state.importPng);
  const exportPng = useSkinStore((state) => state.exportPng);
  const isAiRunning = useSkinStore((state) => state.isAiRunning);
  const draftStatus = useSkinStore((state) => state.draftStatus);
  const autosaveEnabled = useSkinStore((state) => state.autosaveEnabled);
  const setAutosaveEnabled = useSkinStore((state) => state.setAutosaveEnabled);
  const pngInputRef = useRef<HTMLInputElement>(null);
  const projectInputRef = useRef<HTMLInputElement>(null);
  const playerIdInputId = useId();
  const [playerId, setPlayerId] = useState("");
  const [isImportingPlayer, setIsImportingPlayer] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isManualSaving, setIsManualSaving] = useState(false);
  const [openMenu, setOpenMenu] = useState<
    "project" | "new" | "import" | "export" | null
  >(null);
  const importAsNew = useProjectLibraryStore((state) => state.importAsNew);

  const closeMenus = () => setOpenMenu(null);

  /** Exclusive accordion: only one topbar popover at a time. */
  const handleMenuToggle =
    (id: "project" | "new" | "import" | "export") =>
    (event: React.SyntheticEvent<HTMLDetailsElement>) => {
      const willOpen = event.currentTarget.open;
      setOpenMenu(willOpen ? id : openMenu === id ? null : openMenu);
    };

  // Click outside any topbar popover (or Escape) closes the open menu.
  useEffect(() => {
    if (!openMenu) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      const element = target instanceof Element ? target : target.parentElement;
      // Clicks on menu triggers or their panels stay with exclusive toggle.
      if (element?.closest("[data-topbar-menu]")) return;
      closeMenus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenus();
    };

    const dom = globalThis.document;
    dom.addEventListener("pointerdown", handlePointerDown, true);
    dom.addEventListener("keydown", handleKeyDown);
    return () => {
      dom.removeEventListener("pointerdown", handlePointerDown, true);
      dom.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenu]);

  const importPngFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      await importPng(file);
      const imported = useSkinStore.getState().document;
      onNotify(
        `已导入 ${file.name} · ${imported.size}×${imported.size} · ${imported.model === "slim" ? "Slim" : "Classic"}`,
        "success",
      );
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : "皮肤导入失败",
        "error",
      );
    } finally {
      if (pngInputRef.current) pngInputRef.current.value = "";
      closeMenus();
    }
  };

  const importProjectFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const created = await importAsNew(file);
      onNotify(`已导入为新项目「${created.name}」`, "success");
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : "项目导入失败",
        "error",
      );
    } finally {
      if (projectInputRef.current) projectInputRef.current.value = "";
      closeMenus();
    }
  };

  const importPlayer = async () => {
    const submittedId = playerId.trim();
    if (!submittedId) return;
    setIsImportingPlayer(true);
    try {
      const username = await importJavaPlayer(submittedId);
      setPlayerId(username);
      onNotify(`已导入 ${username} 的 Java 版皮肤`, "success");
      closeMenus();
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : "Java 版玩家皮肤导入失败",
        "error",
      );
    } finally {
      setIsImportingPlayer(false);
    }
  };

  const isDocumentReplacementBlocked = isAiRunning || isImportingPlayer;

  const draftStatusCopy = {
    loading: "正在恢复项目",
    saving: "正在保存",
    saved: "工作区已保存",
    paused: "自动保存已关",
    error: "本机保存失败",
  }[draftStatus];

  const toggleAutosave = () => {
    const next = !autosaveEnabled;
    setAutosaveEnabled(next);
    onNotify(next ? "已开启自动保存" : "已关闭自动保存（可用「立即保存」）");
  };

  const saveNow = async () => {
    setIsManualSaving(true);
    try {
      const { saveWorkspaceNow } = await import("../store/skin-store");
      await saveWorkspaceNow();
      onNotify("已保存当前项目", "success");
    } catch (error) {
      onNotify(error instanceof Error ? error.message : "保存失败", "error");
    } finally {
      setIsManualSaving(false);
    }
  };

  const downloadPng = async () => {
    setIsExporting(true);
    try {
      const blob = await exportPng();
      const href = URL.createObjectURL(blob);
      const anchor = globalThis.document.createElement("a");
      anchor.href = href;
      anchor.download = `skinpilot-${document.model}-${Date.now()}.png`;
      anchor.hidden = true;
      globalThis.document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(href), 0);
      onNotify(`${document.size}×${document.size} PNG 已导出`, "success");
      closeMenus();
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : "皮肤导出失败",
        "error",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const downloadBedrockPack = async () => {
    setIsExporting(true);
    try {
      const blob = await exportBedrockSkinPack(document);
      const href = URL.createObjectURL(blob);
      const anchor = globalThis.document.createElement("a");
      anchor.href = href;
      anchor.download = `skinpilot-${document.model}-${document.size}-${Date.now()}.mcpack`;
      anchor.hidden = true;
      globalThis.document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(href), 0);
      onNotify("基岩版皮肤包已导出", "success");
      closeMenus();
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : "基岩版皮肤包导出失败",
        "error",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const downloadProject = async () => {
    setIsExporting(true);
    try {
      await exportCurrentProject();
      onNotify("项目已导出（皮肤、编辑器、撤销栈与对话）", "success");
      closeMenus();
    } catch (error) {
      onNotify(
        error instanceof Error ? error.message : "项目导出失败",
        "error",
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <header className={styles.topbar}>
      <div className={styles.topbarStart}>
        <div className={styles.brand}>
          <span className={styles.brandCube} aria-hidden="true" />
          <div className={styles.brandCopy}>
            <h1 className={styles.brandName}>SkinPilot</h1>
            <span className={styles.brandTagline}>AI PIXEL WORKSHOP</span>
          </div>
        </div>

        <ProjectSwitcher
          onNotify={onNotify}
          open={openMenu === "project"}
          onOpenChange={(next) => setOpenMenu(next ? "project" : null)}
        />

        <div className={styles.saveControls}>
          <label
            className={cx(
              styles.autosaveToggle,
              autosaveEnabled && styles.isOn,
            )}
            title="开启后编辑会自动写入本机当前项目；关闭后仅在切换项目时保存或点「立即保存」"
          >
            <input
              checked={autosaveEnabled}
              onChange={toggleAutosave}
              type="checkbox"
            />
            <span className={styles.autosaveToggleTrack} aria-hidden="true">
              <span className={styles.autosaveToggleThumb} />
            </span>
            <span className={styles.autosaveToggleLabel}>自动保存</span>
          </label>
          <span
            className={styles.saveState}
            role="status"
            title="当前项目（皮肤、编辑器、撤销栈与 AI 对话）可保存到本机 IndexedDB；API 密钥保存在 localStorage"
          >
            <span
              className={cx(
                styles.saveStateDot,
                draftStatus === "loading" && styles.loading,
                draftStatus === "saving" && styles.saving,
                draftStatus === "paused" && styles.paused,
                draftStatus === "error" && styles.error,
              )}
            />
            {draftStatusCopy}
          </span>
          {!autosaveEnabled ? (
            <button
              className={cx(ui.button, ui.buttonSmall, styles.saveNowButton)}
              disabled={
                isManualSaving || isAiRunning || draftStatus === "loading"
              }
              onClick={() => void saveNow()}
              type="button"
            >
              {isManualSaving ? "保存中…" : "立即保存"}
            </button>
          ) : null}
        </div>
      </div>

      <div className={styles.topbarActions}>
        <a
          className={cx(ui.button, styles.topbarButton, styles.githubLink)}
          href="https://github.com/TsukaStore/SkinPilot"
          rel="noreferrer noopener"
          target="_blank"
          aria-label="在 GitHub 查看 SkinPilot 源代码"
          title="github.com/TsukaStore/SkinPilot"
        >
          <Icon name="github" />
          <span className={styles.topbarActionLabel}>GitHub</span>
        </a>

        <details
          className={styles.newMenu}
          data-topbar-menu=""
          aria-disabled={isDocumentReplacementBlocked}
          open={openMenu === "new"}
          onToggle={handleMenuToggle("new")}
        >
          <summary
            className={cx(ui.button, styles.topbarButton)}
            aria-disabled={isDocumentReplacementBlocked}
            aria-label="新建皮肤"
            onClick={(event) => {
              if (isDocumentReplacementBlocked) event.preventDefault();
            }}
          >
            <Icon name="plus" />
            <span className={styles.topbarActionLabel}>新建</span>
          </summary>
          <div className={styles.newMenuPopover} data-testid="new-skin-menu">
            <button
              type="button"
              disabled={isDocumentReplacementBlocked}
              onClick={() => {
                newBlank(undefined, 64);
                closeMenus();
                onNotify("已新建 64×64 空白皮肤");
              }}
            >
              <span
                className={cx(styles.newMenuIcon, styles.blank)}
                aria-hidden="true"
              />
              <span>
                <strong>空白皮肤</strong>
                <small>从透明画布开始</small>
              </span>
            </button>
            <button
              type="button"
              disabled={isDocumentReplacementBlocked}
              onClick={() => {
                newBlank(undefined, 128);
                closeMenus();
                onNotify("已新建 128×128 HD 空白皮肤");
              }}
            >
              <span
                className={cx(styles.newMenuIcon, styles.blank)}
                aria-hidden="true"
              />
              <span>
                <strong>HD 空白皮肤</strong>
                <small>128×128 · 基岩版高清规格</small>
              </span>
            </button>
            <button
              type="button"
              disabled={isDocumentReplacementBlocked}
              onClick={() => {
                newDefault("classic");
                closeMenus();
                onNotify("已加载 Steve 默认皮肤");
              }}
            >
              <span
                className={cx(styles.newMenuIcon, styles.steve)}
                aria-hidden="true"
              />
              <span>
                <strong>Steve 默认皮肤</strong>
                <small>Classic · 标准宽臂</small>
              </span>
            </button>
            <button
              type="button"
              disabled={isDocumentReplacementBlocked}
              onClick={() => {
                newDefault("slim");
                closeMenus();
                onNotify("已加载 Alex 默认皮肤");
              }}
            >
              <span
                className={cx(styles.newMenuIcon, styles.alex)}
                aria-hidden="true"
              />
              <span>
                <strong>Alex 默认皮肤</strong>
                <small>Slim · 纤细手臂</small>
              </span>
            </button>
          </div>
        </details>

        <input
          ref={pngInputRef}
          className="sr-only"
          type="file"
          accept="image/png,.png"
          aria-label="导入 PNG 皮肤"
          disabled={isDocumentReplacementBlocked}
          onChange={(event) => void importPngFile(event.target.files?.[0])}
        />
        <input
          ref={projectInputRef}
          className="sr-only"
          type="file"
          accept="application/json,.json,.skinpilot.json"
          aria-label="导入 SkinPilot 项目"
          disabled={isDocumentReplacementBlocked}
          onChange={(event) => void importProjectFile(event.target.files?.[0])}
        />

        <details
          className={styles.newMenu}
          data-topbar-menu=""
          aria-disabled={isAiRunning}
          open={openMenu === "import"}
          onToggle={handleMenuToggle("import")}
        >
          <summary
            className={cx(ui.button, styles.topbarButton)}
            aria-disabled={isAiRunning}
            aria-label="导入"
            onClick={(event) => {
              if (isAiRunning) event.preventDefault();
            }}
          >
            <Icon name="upload" />
            <span className={styles.topbarActionLabel}>导入</span>
          </summary>
          <div className={cx(styles.newMenuPopover, styles.importMenuPopover)}>
            <form
              className={styles.playerImportForm}
              onSubmit={(event) => {
                event.preventDefault();
                void importPlayer();
              }}
            >
              <div className={styles.playerImportHeading}>
                <label htmlFor={playerIdInputId}>Java 版玩家 ID</label>
                <small>玩家名或 UUID</small>
              </div>
              <div className={styles.playerImportControls}>
                <input
                  id={playerIdInputId}
                  type="text"
                  value={playerId}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  maxLength={36}
                  placeholder="例如 Notch"
                  aria-label="Java 版玩家 ID 或 UUID"
                  disabled={isDocumentReplacementBlocked}
                  onChange={(event) => setPlayerId(event.target.value)}
                />
                <button
                  className={cx(ui.button, ui.buttonClay, ui.buttonSmall)}
                  type="submit"
                  aria-label={
                    isImportingPlayer
                      ? "正在获取 Java 版玩家皮肤"
                      : "获取 Java 版玩家皮肤"
                  }
                  disabled={isDocumentReplacementBlocked || !playerId.trim()}
                >
                  {isImportingPlayer ? "查询中" : "获取"}
                </button>
              </div>
              <small className={styles.playerImportSource}>
                资料经 PlayerDB 查询，纹理由 Minecraft 提供
              </small>
            </form>
            <button
              type="button"
              disabled={isDocumentReplacementBlocked}
              onClick={() => pngInputRef.current?.click()}
            >
              <span>
                <strong>PNG 皮肤</strong>
                <small>64×32 / 64×64 / 128×128</small>
              </span>
            </button>
            <button
              type="button"
              disabled={isDocumentReplacementBlocked}
              onClick={() => projectInputRef.current?.click()}
            >
              <span>
                <strong>SkinPilot 项目</strong>
                <small>导入为新项目并切换</small>
              </span>
            </button>
          </div>
        </details>

        <details
          className={styles.newMenu}
          data-topbar-menu=""
          aria-disabled={isExporting}
          open={openMenu === "export"}
          onToggle={handleMenuToggle("export")}
        >
          <summary
            className={cx(ui.button, ui.buttonPrimary, styles.topbarButton)}
            aria-disabled={isExporting}
            aria-label={isExporting ? "正在导出" : "导出"}
            onClick={(event) => {
              if (isExporting) event.preventDefault();
            }}
          >
            <Icon name="download" />
            <span className={styles.topbarActionLabel}>
              {isExporting ? "处理中" : "导出"}
            </span>
          </summary>
          <div className={styles.newMenuPopover}>
            <button
              type="button"
              aria-label={`导出 ${document.size}×${document.size} PNG 皮肤`}
              disabled={isExporting}
              onClick={() => void downloadPng()}
            >
              <span>
                <strong>
                  {document.size}×{document.size} PNG 皮肤
                </strong>
                <small>保留原始 RGBA 像素</small>
              </span>
            </button>
            <button
              type="button"
              aria-label="导出基岩版皮肤包"
              disabled={isExporting}
              onClick={() => void downloadBedrockPack()}
            >
              <span>
                <strong>基岩版 .mcpack</strong>
                <small>
                  {document.model === "slim" ? "Slim" : "Classic"} 几何 ·
                  可直接导入
                </small>
              </span>
            </button>
            <button
              type="button"
              disabled={isExporting || isAiRunning}
              onClick={() => void downloadProject()}
            >
              <span>
                <strong>SkinPilot 项目</strong>
                <small>完整还原当前工作区</small>
              </span>
            </button>
          </div>
        </details>
      </div>
    </header>
  );
}
